import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import RichTextEditor from "../components/RichTextEditor";
import { PolicyContent } from "../components/PolicyContent";
import PolicyAssignments from "../components/PolicyAssignments";
import { bodyToHtml } from "../lib/sanitizeHtml";
import { formatUK } from "../lib/ukdate";
import { WsDetails } from "../components/workspace/WsDetails";
import { WsBooklets } from "../components/workspace/WsBooklets";
import { WsCompare } from "../components/workspace/WsCompare";
import { WsPublish } from "../components/workspace/WsPublish";
import { WsReplacePdf } from "../components/workspace/WsReplacePdf";
import { ArrowLeft, GitDiff, Plus, RocketLaunch, Trash, FilePdf, FloppyDisk, Eye } from "@phosphor-icons/react";

const WORKING = ["draft", "review", "approved"];
const Card = ({ title, children, testid }) => (
  <section data-testid={testid} className="bg-white border border-slate-200 rounded-xl p-4">
    <h3 className="font-display font-bold text-slate-800 mb-3 text-sm uppercase tracking-wide">{title}</h3>{children}
  </section>
);

function RevisionHistory({ revs, current, onView, onCompare }) {
  return (
    <div className="flex flex-col divide-y divide-slate-100" data-testid="ws-revisions">
      {revs.map((r) => (
        <div key={r.id} data-testid={`ws-rev-${r.id}`} className="py-2 flex items-center gap-2 text-sm">
          <span className="font-mono font-semibold text-slate-700 w-14">Rev {r.revision}</span>
          <span className={`text-[10px] font-bold uppercase rounded px-1.5 py-0.5 ${r.status === "published" ? "bg-emerald-50 text-emerald-800" : WORKING.includes(r.status) ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-500"}`}>{r.is_current ? "issued" : r.status}</span>
          {r.is_pdf && <FilePdf size={14} className="text-slate-400" />}
          <span className="text-xs text-slate-500 flex-1 truncate">{r.effective_date ? `Effective ${formatUK(r.effective_date)}` : ""}{r.change_summary ? ` · ${r.change_summary}` : ""}</span>
          <button data-testid={`ws-rev-view-${r.id}`} onClick={() => onView(r)} className="text-slate-500 hover:text-slate-900" title="Read this revision"><Eye size={16} /></button>
          {current && r.id !== current.id && <button data-testid={`ws-rev-compare-${r.id}`} onClick={() => onCompare(r)} className="text-slate-500 hover:text-slate-900" title="Compare with issued"><GitDiff size={16} /></button>}
        </div>
      ))}
    </div>
  );
}

export default function PolicyWorkspacePage() {
  const { id } = useParams();
  const [ws, setWs] = useState(null);
  const [html, setHtml] = useState("");
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [viewRev, setViewRev] = useState(null);
  const [cmp, setCmp] = useState(null);
  const [pub, setPub] = useState(false);
  const [pdfOpen, setPdfOpen] = useState(false);

  const load = useCallback(() => api.get(`/policies/${id}/workspace`).then((r) => {
    setWs(r.data); setViewRev(null);
    const w = r.data.revisions.find((x) => WORKING.includes(x.status)) || r.data.revisions.find((x) => x.is_current) || r.data.revisions[0];
    setHtml(bodyToHtml(w?.body)); setDirty(false);
  }).catch((e) => toast.error(e?.response?.data?.detail || "Could not load document")), [id]);
  useEffect(() => { load(); }, [load]);

  if (!ws) return <p className="text-slate-400 text-sm">Loading…</p>;
  const d = ws.document;
  const working = ws.revisions.find((r) => WORKING.includes(r.status));
  const current = ws.revisions.find((r) => r.is_current && r.status === "published");
  const shown = viewRev || working || current || ws.revisions[0];
  const editable = !viewRev && working && ["draft", "review"].includes(working.status) && !working.is_pdf;
  const isPdfDoc = !!(shown?.is_pdf);

  const run = async (fn, ok) => {
    if (busy) return; setBusy(true);
    try { await fn(); if (ok) toast.success(ok); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); } finally { setBusy(false); }
  };
  const saveDraft = () => run(() => api.put(`/policies/revisions/${working.id}`, { body: { html } }), "Draft saved");
  const newRev = () => run(() => api.post(`/policies/${id}/revisions/new`, {}), "New draft revision started");
  const discard = () => { if (window.confirm("Discard this unpublished draft? The issued version and its acknowledgements are kept.")) run(() => api.delete(`/policies/revisions/${working.id}`), "Draft discarded"); };
  const openPublish = async () => { if (dirty) await run(() => api.put(`/policies/revisions/${working.id}`, { body: { html } })); setPub(true); };

  return (
    <div className="flex flex-col gap-4" data-testid="policy-workspace">
      <Link to="/policy-manager" data-testid="ws-back" className="text-sm text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 self-start"><ArrowLeft size={14} /> All documents</Link>
      <header className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-0">
          <h1 data-testid="ws-heading" className="font-display text-2xl sm:text-3xl font-black tracking-tight text-slate-900 truncate">{d.title}</h1>
          <div className="font-mono text-xs text-slate-500 mt-1">{d.ref} · {(d.kind || "").replace(/_/g, " ")}
            {current ? ` · Issued Rev ${current.revision}${current.effective_date ? ` (effective ${formatUK(current.effective_date)})` : ""}` : " · Not yet published"}
            {working ? ` · Working Rev ${working.revision} (${working.status})` : ""}</div>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="ws-actions">
          {!working && <Button data-testid="ws-new-rev" size="sm" disabled={busy} onClick={newRev} className="gap-1 bg-slate-800 hover:bg-slate-900"><Plus size={14} />New revision</Button>}
          {!working && isPdfDoc && <Button data-testid="ws-replace-pdf-btn" size="sm" variant="outline" onClick={() => setPdfOpen(true)} className="gap-1"><FilePdf size={14} />Replace PDF</Button>}
          {editable && <Button data-testid="ws-save-draft" size="sm" variant="outline" disabled={busy || !dirty} onClick={saveDraft} className="gap-1"><FloppyDisk size={14} />Save draft</Button>}
          {working && current && <Button data-testid="ws-compare-btn" size="sm" variant="outline" onClick={() => setCmp({ a: current.id, b: working.id })} className="gap-1"><GitDiff size={14} />Compare with issued</Button>}
          {working && <Button data-testid="ws-discard" size="sm" variant="outline" disabled={busy} onClick={discard} className="gap-1 text-red-600 border-red-200"><Trash size={14} />Discard draft</Button>}
          {working && <Button data-testid="ws-publish-btn" size="sm" disabled={busy} onClick={openPublish} className="gap-1 bg-emerald-700 hover:bg-emerald-800"><RocketLaunch size={14} />Review &amp; publish</Button>}
        </div>
      </header>

      <div className="grid lg:grid-cols-[1fr_360px] gap-4 items-start">
        <section className="bg-white border border-slate-200 rounded-xl p-4 min-w-0" data-testid="ws-content">
          <div className="flex items-center justify-between mb-3 text-xs">
            <span className="font-bold uppercase tracking-wide text-slate-500">{viewRev ? `Reading Rev ${viewRev.revision} (${viewRev.is_current ? "issued" : viewRev.status})` : working ? `Working Rev ${working.revision}` : current ? `Issued Rev ${current.revision} — start a new revision to edit` : "Content"}</span>
            {viewRev && <button data-testid="ws-back-to-working" onClick={() => setViewRev(null)} className="text-emerald-700 font-semibold">Back to {working ? "working draft" : "issued"}</button>}
          </div>
          {shown?.is_pdf ? <PolicyContent isPdf pdfUrl={shown.pdf_url} pdfFilename={shown.body?.filename} pdfAccess />
            : editable ? <RichTextEditor html={html} onChange={(h) => { setHtml(h); setDirty(true); }} />
            : <PolicyContent body={shown?.body} />}
          {working?.status === "approved" && !viewRev && <p className="text-xs text-blue-700 mt-2">Approved and locked — publish it, or discard and start again.</p>}
        </section>
        <aside className="flex flex-col gap-4">
          <Card title="Details" testid="ws-card-details"><WsDetails ws={ws} onSaved={load} /></Card>
          <Card title="Booklets" testid="ws-card-booklets"><WsBooklets rid={id} booklets={ws.booklets} onSaved={load} /></Card>
          <Card title="Revisions" testid="ws-card-revisions"><RevisionHistory revs={ws.revisions} current={current} onView={setViewRev} onCompare={(r) => setCmp({ a: current.id, b: r.id })} /></Card>
        </aside>
      </div>
      <Card title="Audience — who must acknowledge" testid="ws-card-audience"><PolicyAssignments fixedRid={id} /></Card>

      {cmp && <WsCompare a={cmp.a} b={cmp.b} onClose={() => setCmp(null)} />}
      {pub && working && <WsPublish rid={id} working={working} currentId={current?.id} onClose={() => setPub(false)} onDone={() => { setPub(false); load(); }} />}
      {pdfOpen && <WsReplacePdf rid={id} onClose={() => setPdfOpen(false)} onDone={() => { setPdfOpen(false); load(); }} />}
    </div>
  );
}
